import { Alert, Autocomplete, Box, Card, CardContent, Chip, Divider, FormControlLabel, Skeleton, Switch, TextField, Typography, useTheme } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FormHandles } from '@unform/core';
import { Form } from '../../../shared/forms';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { VTextField } from '../../../shared/forms';
import { Button } from '../../../shared/components';
import getValidationErrors from '../../../shared/helpers/getValidationErrors';
import { whatsAppService, IUpdateWhatsAppConfig, IWhatsAppConfig } from '../../../shared/services/api/whatsapp';
import { IPropsBusinessPage } from '../Business';
import { feedback, finishRequest } from '../../../shared/services';

const VARIABLE_HINT = 'Variáveis disponíveis: {{nome}} {{data}} {{horário}} {{serviço}} {{profissional}} {{salão}} {{endereço}}';

const schema = Yup.object().shape({
  systemPrompt: Yup.string(),
  messageTtlHours: Yup.number().min(1).max(720),
  maxDailyMessagesPerUser: Yup.number().min(1).max(1000),
  cooldownSeconds: Yup.number().min(3).max(60),
  optOutPhrases: Yup.string(),
  phoneFilterMode: Yup.boolean(),
  businessHoursOnly: Yup.boolean(),
  sendConfirmation: Yup.boolean(),
  sendReminder: Yup.boolean(),
  sendFeedback: Yup.boolean(),
  reminderHoursBefore: Yup.number().min(1).max(720),
  confirmationMessage: Yup.string().nullable(),
  reminderMessage: Yup.string().nullable(),
  feedbackMessage: Yup.string().nullable(),
});

export const WhatsAppBotConfig: React.FC<IPropsBusinessPage> = ({ business, refetch }) => {
  const formRef = useRef<FormHandles>(null);
  const theme = useTheme();
  const queryClient = useQueryClient();

  const { data: config, isFetching } = useQuery({
    queryKey: ['whatsapp-config', business.id],
    queryFn: () => whatsAppService.getConfig(business.id),
  });

  const [sendConfirmationOn, setSendConfirmationOn] = useState(false);
  const [sendReminderOn, setSendReminderOn] = useState(false);
  const [sendFeedbackOn, setSendFeedbackOn] = useState(false);
  const [businessHoursOnlyOn, setBusinessHoursOnlyOn] = useState(false);
  const [phoneFilterModeOn, setPhoneFilterModeOn] = useState(false);
  const [allowedPhones, setAllowedPhones] = useState<string[]>([]);
  const [blockedPhones, setBlockedPhones] = useState<string[]>([]);

  const parsePhones = (val: string | undefined | null): string[] =>
    (val ?? '').split('|').filter(Boolean).map(s => s.trim());

  const joinPhones = (arr: string[]): string => arr.join('|');

  const MIN_PHONE_LENGTH = 11;

  const handlePhoneChange = (
    currentList: string[],
    setter: React.Dispatch<React.SetStateAction<string[]>>,
  ) => (_: unknown, newValue: string[]) => {
    if (newValue.length > currentList.length) {
      const added = newValue.find(v => !currentList.includes(v));
      if (added && added.length < MIN_PHONE_LENGTH) {
        feedback('O número deve ter no mínimo 11 dígitos (apenas DDD + número)', 'warning');
        return;
      }
    }
    setter(newValue);
  };

  const { mutate: updateConfig, isLoading: isSaving } = useMutation(
    (data: IUpdateWhatsAppConfig) => whatsAppService.updateConfig(business.id, data),
    {
      onSuccess: () => {
        finishRequest('Configuração salva!', 'success', () => {
          queryClient.invalidateQueries(['whatsapp-config', business.id]);
          refetch();
        });
      },
      onError: (error: Error) => finishRequest(error.message, 'error'),
    }
  );

  const { mutate: toggleEnabled } = useMutation(
    (enabled: boolean) => whatsAppService.updateConfig(business.id, { enabled }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['whatsapp-config', business.id]);
      },
      onError: (error: Error) => feedback(error.message, 'error'),
    }
  );

  const normalizeFormData = (raw: Record<string, unknown>) => ({
    systemPrompt: raw.systemPrompt as string | undefined,
    messageTtlHours: Number(raw.messageTtlHours),
    maxDailyMessagesPerUser: Number(raw.maxDailyMessagesPerUser),
    cooldownSeconds: Number(raw.cooldownSeconds),
    businessHoursOnly: raw.businessHoursOnly === true || raw.businessHoursOnly === 'true',
    optOutPhrases: raw.optOutPhrases as string | undefined,
    phoneFilterMode: phoneFilterModeOn,
    allowedPhoneNumbers: phoneFilterModeOn ? joinPhones(allowedPhones) || null : null,
    blockedPhoneNumbers: !phoneFilterModeOn ? joinPhones(blockedPhones) || null : null,
    sendConfirmation: raw.sendConfirmation === true || raw.sendConfirmation === 'true',
    sendReminder: raw.sendReminder === true || raw.sendReminder === 'true',
    sendFeedback: raw.sendFeedback === true || raw.sendFeedback === 'true',
    reminderHoursBefore: Number(raw.reminderHoursBefore),
    confirmationMessage: (raw.confirmationMessage as string) || null,
    reminderMessage: (raw.reminderMessage as string) || null,
    feedbackMessage: (raw.feedbackMessage as string) || null,
  });

  const handleSubmit = useCallback(async (formData: Record<string, unknown>) => {
    try {
      formRef.current?.setErrors({});
      await schema.validate(formData, { abortEarly: false });

      updateConfig(normalizeFormData(formData));
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err);
        formRef.current?.setErrors(errors);
      }
    }
  }, [updateConfig, business.id, phoneFilterModeOn, allowedPhones, blockedPhones]);

  // Preencher formulário quando config carregar
  useEffect(() => {
    if (config && 'systemPrompt' in config) {
      formRef.current?.setData({
        systemPrompt: config.systemPrompt || '',
        messageTtlHours: config.messageTtlHours,
        maxDailyMessagesPerUser: config.maxDailyMessagesPerUser,
        cooldownSeconds: config.cooldownSeconds,
        businessHoursOnly: config.businessHoursOnly,
        optOutPhrases: config.optOutPhrases,
        sendConfirmation: config.sendConfirmation,
        sendReminder: config.sendReminder,
        sendFeedback: config.sendFeedback,
        reminderHoursBefore: config.reminderHoursBefore,
        confirmationMessage: config.confirmationMessage || '',
        reminderMessage: config.reminderMessage || '',
        feedbackMessage: config.feedbackMessage || '',
      });

      setSendConfirmationOn(config.sendConfirmation);
      setSendReminderOn(config.sendReminder);
      setSendFeedbackOn(config.sendFeedback);
      setBusinessHoursOnlyOn(config.businessHoursOnly);
      setPhoneFilterModeOn(config.phoneFilterMode);
      setAllowedPhones(parsePhones(config.allowedPhoneNumbers));
      setBlockedPhones(parsePhones(config.blockedPhoneNumbers));
    }
  }, [config]);

  const isConfigured = config && 'systemPrompt' in config;
  const isEnabled = isConfigured && !!(config as IWhatsAppConfig).enabled;

  if (isFetching) {
    return (
      <Box>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Skeleton variant='rectangular' height={80} sx={{ borderRadius: 1 }} />
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Skeleton variant='rectangular' height={400} sx={{ borderRadius: 1 }} />
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      {/* Ativar/Desativar */}
      <Card sx={{ mb: 2, bgcolor: theme.palette.background.paper }}>
        <CardContent>
          <Box display='flex' alignItems='center' justifyContent='space-between'>
            <Box>
              <Typography variant='h6'>Bot WhatsApp</Typography>
              <Typography variant='body2' color='text.secondary'>
                {isEnabled
                  ? 'O bot está ativo e respondendo mensagens'
                  : 'O bot está desativado'}
              </Typography>
            </Box>
            <Switch
              checked={isEnabled}
              onChange={(_, checked) => toggleEnabled(checked)}
              disabled={isFetching || !isConfigured}
              color='primary'
            />
          </Box>
        </CardContent>
      </Card>

      <Form ref={formRef} onSubmit={handleSubmit}>
        {/* Configurações do Bot */}
        <Card sx={{ bgcolor: theme.palette.background.paper }}>
          <CardContent>
            <Typography variant='h6' gutterBottom>Configurações do Bot</Typography>
            <Divider sx={{ mb: 2 }} />

            <VTextField
              name='systemPrompt'
              label='Prompt do sistema'
              multiline
              minRows={4}
              maxRows={12}
              placeholder='Personalize o comportamento do bot...'
              fullWidth
              sx={{ mb: 2 }}
            />

            <Box display='flex' gap={2} flexWrap='wrap' sx={{ mb: 2 }}>
              <Box>
                <VTextField
                  name='messageTtlHours'
                  label='Memória do bot (horas)'
                  type='number'
                  helperText='Por quanto tempo o bot lembra da conversa. Mínimo recomendado: 24h'
                  sx={{ minWidth: 180 }}
                />
                <Alert severity='warning' sx={{ mt: 0.5, py: 0, fontSize: '0.75rem' }}>
                  Menos de 24h o bot pode esquecer a conversa do cliente do dia anterior
                </Alert>
              </Box>
              <Box>
                <VTextField
                  name='maxDailyMessagesPerUser'
                  label='Msgs por dia por cliente'
                  type='number'
                  helperText='Máximo de mensagens que o bot envia para o mesmo cliente por dia'
                  sx={{ minWidth: 180 }}
                />
                <Alert severity='warning' sx={{ mt: 0.5, py: 0, fontSize: '0.75rem' }}>
                  Acima de 100 pode aumentar risco de banimento no WhatsApp
                </Alert>
              </Box>
              <Box>
                <VTextField
                  name='cooldownSeconds'
                  label='Intervalo entre respostas (s)'
                  type='number'
                  helperText='Aguarda N segundos antes de responder outra mensagem do mesmo cliente. 3s-60s'
                  sx={{ minWidth: 180 }}
                />
                <Alert severity='warning' sx={{ mt: 0.5, py: 0, fontSize: '0.75rem' }}>
                  Menos de 3s pode causar banimento por excesso de mensagens
                </Alert>
              </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    name='businessHoursOnly'
                    checked={businessHoursOnlyOn}
                    onChange={(_, checked) => {
                      setBusinessHoursOnlyOn(checked);
                      formRef.current?.setFieldValue('businessHoursOnly', checked);
                    }}
                  />
                }
                label='Apenas em horário comercial'
              />
              <Typography variant='caption' color='text.secondary' sx={{ ml: 6, display: 'block' }}>
                Evita enviar mensagens fora do horário de funcionamento. Reduz risco de banimento.
              </Typography>
            </Box>

            <VTextField
              name='optOutPhrases'
              label='Frases de opt-out (separadas por |)'
              placeholder='sair|parar|não quero mais|cancelar atendimento|falar com atendente|atendente|humano|falar com humano'
              fullWidth
              sx={{ mb: 2 }}
            />

            {/* ── Filtro por contato ── */}
            <Divider sx={{ my: 2 }} />
            <Typography variant='subtitle1' gutterBottom>Filtro por contato</Typography>

            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={phoneFilterModeOn}
                    onChange={(_, checked) => {
                      setPhoneFilterModeOn(checked);
                      formRef.current?.setFieldValue('phoneFilterMode', checked);
                    }}
                  />
                }
                label='Filtrar apenas contatos'
              />
              <Typography variant='caption' color='text.secondary' sx={{ ml: 6, display: 'block' }}>
                {phoneFilterModeOn
                  ? 'SÓ esses números receberão resposta automática do bot'
                  : 'Esses números NUNCA receberão resposta automática do bot'}
              </Typography>
            </Box>

            {phoneFilterModeOn ? (
              <Autocomplete
                freeSolo
                multiple
                options={[]}
                value={allowedPhones}
                onChange={handlePhoneChange(allowedPhones, setAllowedPhones)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      size='small'
                      {...getTagProps({ index })}
                      key={option}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Contatos permitidos (allowlist)'
                    placeholder='51999999999'
                    helperText='Digite apenas DDD + número (mín. 11 dígitos). O bot completa o +55.'
                  />
                )}
                sx={{ mb: 2 }}
              />
            ) : (
              <Autocomplete
                freeSolo
                multiple
                options={[]}
                value={blockedPhones}
                onChange={handlePhoneChange(blockedPhones, setBlockedPhones)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      size='small'
                      {...getTagProps({ index })}
                      key={option}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Contatos bloqueados (blocklist)'
                    placeholder='51999999999'
                    helperText='Digite apenas DDD + número (mín. 11 dígitos). Esses NÃO receberão resposta.'
                  />
                )}
                sx={{ mb: 2 }}
              />
            )}
          </CardContent>
        </Card>

        {/* ── Mensagens para Clientes ── */}
        <Card sx={{ mt: 2, bgcolor: theme.palette.background.paper }}>
          <CardContent>
            <Typography variant='h6' gutterBottom>Mensagens para Clientes</Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              As mensagens são enviadas via WhatsApp <em>apenas</em> para clientes que tenham
              o telefone confirmado (vínculo com User + PhoneConfirmed). Se o template estiver
              vazio, será usada a mensagem padrão do sistema.
            </Typography>

            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    name='sendConfirmation'
                    checked={sendConfirmationOn}
                    onChange={(_, checked) => {
                      setSendConfirmationOn(checked);
                      formRef.current?.setFieldValue('sendConfirmation', checked);
                    }}
                  />
                }
                label='Confirmação de agendamento'
              />
              <Typography variant='caption' color='text.secondary' sx={{ ml: 6, display: 'block', mb: 1 }}>
                Enviado ao criar um novo agendamento ou ao confirmar um pendente
              </Typography>
              {sendConfirmationOn && (
                <VTextField
                  name='confirmationMessage'
                  label='Template de confirmação'
                  multiline
                  minRows={3}
                  maxRows={6}
                  placeholder='Deixe vazio para usar a mensagem padrão'
                  fullWidth
                  helperText={VARIABLE_HINT}
                />
              )}
            </Box>

            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    name='sendReminder'
                    checked={sendReminderOn}
                    onChange={(_, checked) => {
                      setSendReminderOn(checked);
                      formRef.current?.setFieldValue('sendReminder', checked);
                    }}
                  />
                }
                label='Lembrete de agendamento'
              />
              <Typography variant='caption' color='text.secondary' sx={{ ml: 6, display: 'block', mb: 1 }}>
                Enviado X horas antes do horário marcado
              </Typography>
              {sendReminderOn && (
                <>
                  <VTextField
                    name='reminderHoursBefore'
                    label='Enviar lembrete quantas horas antes?'
                    type='number'
                    sx={{ minWidth: 180, ml: 6, mb: 1 }}
                  />
                  <VTextField
                    name='reminderMessage'
                    label='Template de lembrete'
                    multiline
                    minRows={3}
                    maxRows={6}
                    placeholder='Deixe vazio para usar a mensagem padrão'
                    fullWidth
                    helperText={VARIABLE_HINT}
                  />
                </>
              )}
            </Box>

            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    name='sendFeedback'
                    checked={sendFeedbackOn}
                    onChange={(_, checked) => {
                      setSendFeedbackOn(checked);
                      formRef.current?.setFieldValue('sendFeedback', checked);
                    }}
                  />
                }
                label='Feedback pós-atendimento'
              />
              <Typography variant='caption' color='text.secondary' sx={{ ml: 6, display: 'block', mb: 1 }}>
                Enviado 15 minutos após o horário do atendimento
              </Typography>
              {sendFeedbackOn && (
                <VTextField
                  name='feedbackMessage'
                  label='Template de feedback'
                  multiline
                  minRows={3}
                  maxRows={6}
                  placeholder='Deixe vazio para usar a mensagem padrão'
                  fullWidth
                  helperText={VARIABLE_HINT}
                />
              )}
            </Box>
          </CardContent>
        </Card>

        <Box display='flex' justifyContent='flex-end' sx={{ mt: 2 }}>
          <Button
            type='submit'
            variant='contained'
            isLoading={isSaving}
          >
            Salvar Configuração
          </Button>
        </Box>
      </Form>
    </Box>
  );
};
